import './ProjectListToolbar.scss';
import { useForm, useFormWatch } from '@form-ts/react';
import clsx from 'clsx';
import { flow } from 'fp-ts/function';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Button } from 'src/modules/common/components/Button';
import { Popover } from 'src/modules/common/components/Popover';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { INTEGER_ONE } from 'src/modules/common/constants/integer';
import { CircleFilledIcon } from 'src/modules/common/icons/CircleFilledIcon';
import { DownloadIcon } from 'src/modules/common/icons/DownloadIcon';
import { FilterIcon } from 'src/modules/common/icons/FilterIcon';
import { GridViewIcon } from 'src/modules/common/icons/GridViewIcon';
import { ListViewIcon } from 'src/modules/common/icons/ListViewIcon';
import { PlusIcon } from 'src/modules/common/icons/PlusIcon';
import { SettingsIcon } from 'src/modules/common/icons/SettingsIcon';
import { UploadIcon } from 'src/modules/common/icons/UploadIcon';
import { Future } from 'src/modules/common/types/Future';
import { SearchField } from 'src/modules/form/components/SearchField';
import { FormError } from 'src/modules/form/types/FormError';
import { PROJECT_LIST_DEFAULT_SORT } from 'src/modules/project-list/constants/sort';
import { ProjectListColumn } from 'src/modules/project-list/types/ProjectListColumn';
import { ProjectListFilter } from 'src/modules/project-list/types/ProjectListFilter';
import { ProjectListQuery } from 'src/modules/project-list/types/ProjectListQuery';
import { ProjectListViewMode } from 'src/modules/project-list/types/ProjectListViewMode';
import { buildProjectListQuery } from 'src/modules/project-list/utils/query';
import { ProjectListColumnSelector } from 'src/modules/project-list/views/ProjectListColumnSelector';
import { ProjectListFilterDropdown } from 'src/modules/project-list/views/ProjectListFilterDropdown';
import { useDebouncedCallback } from 'use-debounce';

type Props = {
  readonly query: ProjectListQuery;
  readonly exporting: Future<void> | null;
  readonly columns: ReadonlyArray<ProjectListColumn>;
  readonly onCreate: () => void;
  readonly onImport: () => void;
  readonly onExport: () => void;
  readonly onFilter: (filter: ProjectListFilter) => void;
  readonly onChangeColumns: (columns: ReadonlyArray<ProjectListColumn>) => void;
};

export const ProjectListToolbar = memo(({
  query,
  exporting,
  columns,
  onCreate,
  onImport,
  onExport,
  onFilter,
  onChangeColumns,
}: Props): React.ReactElement => {
  const intl = useIntl();

  const [isFiltersPopoverOpened, setFiltersPopoverOpened] = useState(false);
  const [isColumnsDropdownOpened, setColumnsDropdownOpened] = useState(false);

  const form = useForm<ProjectListFilter, FormError>('project.filter', {
    reinitialize: true,
    initialValues: query.filter,
  });

  const isFilterApplied = Object.entries(query.filter)
    .filter(([filterName]) => filterName !== 'term')
    .some(([, value]) => {
      if (value instanceof Set) {
        return value.size > 0;
      }
      return Boolean(value);
    });

  const count = useFormWatch(form, flow(form.values.get, getAppliedFilterCount));

  const handleFilterDebounced = useDebouncedCallback(onFilter, 300);

  const handleFiltersDropdownOpen = useCallback(() => {
    setFiltersPopoverOpened(true);
  }, []);

  const handleFiltersDropdownClose = useCallback(() => {
    setFiltersPopoverOpened(false);
    onFilter(form.currentState.values);
  }, [form, onFilter]);

  const handleFiltersDropdownApply = useCallback(() => {
    onFilter(form.currentState.values);
  }, [form, onFilter]);

  const handleColumnsDropdownOpen = useCallback(() => {
    setColumnsDropdownOpened(true);
  }, []);

  const handleQueryTermChange = useCallback(() => {
    handleFilterDebounced(form.currentState.values);
  }, [form.currentState.values, handleFilterDebounced]);

  useEffect(() => () => {
    handleFilterDebounced.cancel();
  }, [handleFilterDebounced]);

  return (
    <div className="bp-project-list-toolbar">
      <div className="bp-project-list-toolbar__content">
        <div className="bp-project-list-toolbar__title-wrapper">
          <h2 className="bp-project-list-toolbar__title">
            <FormattedMessage id="projectList/pageHeader"/>
          </h2>
        </div>

        <div className="bp-project-list-toolbar__controls">
          <div className="bp-project-list-toolbar__controls-actions">
            <Tooltip
              id="projectList/toolbar/viewMode/list"
              value={intl.formatMessage({ id: 'projectList/toolbar/viewMode/list' })}
            >
              <Button
                as={Link}
                to={{
                  search: buildProjectListQuery({
                    ...query,
                    view: ProjectListViewMode.LIST,
                    sort: PROJECT_LIST_DEFAULT_SORT[ProjectListViewMode.LIST],
                    page: INTEGER_ONE,
                  }).toString(),
                }}
                iconOnly={true}
                kind="ghost"
                size="md"
                onClick={onImport}
              >
                <div className="bp-project-list-toolbar__controls-icon">
                  <ListViewIcon/>
                </div>
              </Button>
            </Tooltip>

            <Tooltip
              id="projectList/toolbar/viewMode/grid"
              value={intl.formatMessage({ id: 'projectList/toolbar/viewMode/grid' })}
            >
              <Button
                as={Link}
                to={{
                  search: buildProjectListQuery({
                    ...query,
                    view: ProjectListViewMode.GRID,
                    sort: PROJECT_LIST_DEFAULT_SORT[ProjectListViewMode.GRID],
                    page: INTEGER_ONE,
                  }).toString(),
                }}
                iconOnly={true}
                kind="ghost"
                size="md"
              >
                <div className="bp-project-list-toolbar__controls-icon">
                  <GridViewIcon/>
                </div>
              </Button>
            </Tooltip>
          </div>

          <div className="bp-project-list-toolbar__controls-actions">
            <Tooltip id="projectList/toolbar/upload" value={intl.formatMessage({ id: 'projectList/toolbar/upload' })}>
              <Button
                iconOnly={true}
                kind="ghost"
                size="md"
                onClick={onImport}
              >
                <div className="bp-project-list-toolbar__controls-icon">
                  <UploadIcon/>
                </div>
              </Button>
            </Tooltip>

            <Tooltip
              id="projectList/toolbar/download"
              value={intl.formatMessage({ id: 'projectList/toolbar/download' })}
            >
              <Button
                iconOnly={true}
                size="md"
                kind="ghost"
                onClick={onExport}
                loading={exporting?.state === 'pending'}
                icon={(
                  <div className="bp-project-list-toolbar__controls-icon">
                    <DownloadIcon/>
                  </div>
                )}
              />
            </Tooltip>

            <Popover
              id="projectList/toolbar/filter/popover"
              opened={isFiltersPopoverOpened}
              place="bottom-end"
              setOpened={setFiltersPopoverOpened}
              value={(
                <ProjectListFilterDropdown
                  form={form}
                  onApply={handleFiltersDropdownApply}
                  onClose={handleFiltersDropdownClose}
                />
              )}
              onClose={handleFiltersDropdownClose}
            >
              <Tooltip
                id="projectList/toolbar/filter"
                value={intl.formatMessage({ id: 'projectList/toolbar/filter' }, { count })}
              >
                <Button
                  iconOnly={true}
                  size="md"
                  kind="ghost"
                  onClick={handleFiltersDropdownOpen}
                >
                  <div className="bp-project-list-toolbar__controls-icon">
                    <FilterIcon/>
                  </div>

                  {isFilterApplied && (
                    <div className="bp-project-list-toolbar__badge">
                      <CircleFilledIcon/>
                    </div>
                  )}
                </Button>
              </Tooltip>
            </Popover>

            <Popover
              id="projectList/toolbar/columns/popover"
              place="bottom-end"
              opened={isColumnsDropdownOpened}
              setOpened={setColumnsDropdownOpened}
              value={(
                <ProjectListColumnSelector
                  columns={columns}
                  onChange={onChangeColumns}
                />
              )}
            >
              <Tooltip
                id="projectList/toolbar/columns"
                value={intl.formatMessage({ id: 'projectList/toolbar/columns' })}
              >
                <Button
                  iconOnly={true}
                  size="md"
                  kind="ghost"
                  onClick={handleColumnsDropdownOpen}
                  className={clsx({
                    'bp-project-list-toolbar__controls-button': true,
                    'bp-project-list-toolbar__controls-button--opened': isColumnsDropdownOpened,
                  })}
                >
                  <div className="bp-project-list-toolbar__controls-icon">
                    <SettingsIcon/>
                  </div>
                </Button>
              </Tooltip>
            </Popover>

            <SearchField
              size="md"
              className="bp-project-list-toolbar__controls-search"
              placeholder={intl.formatMessage({ id: 'projectList/searchPlaceholder' })}
              field={form.field.at('term')}
              onChange={handleQueryTermChange}
              description={null}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
            />

            <Button
              className="bp-project-list-toolbar__controls-new-project"
              kind="primary"
              size="md"
              fullWidth={true}
              onClick={onCreate}
              data-test-id="create-project-button"
            >
              <FormattedMessage id="projectList/newProject"/>

              <div className="bp-project-list-toolbar__controls-icon">
                <PlusIcon/>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

function getAppliedFilterCount(filter: ProjectListFilter): number {
  return (
    (filter.buildingType.size > 0 ? 1 : 0) +
    (filter.country.size > 0 ? 1 : 0) +
    (filter.city.size > 0 ? 1 : 0) +
    (filter.ratingActual.size > 0 ? 1 : 0) +
    (filter.ratingFuture.size > 0 ? 1 : 0)
  );
}
